import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  ArrowLeft, 
  Percent,
  CheckCircle,
  Info
} from 'lucide-react';
import Footer from '@/components/Footer';

const FeesPage = () => {
  const navigate = useNavigate();

  return (
    <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
      {/* Navigation */}
      <nav className='bg-white border-b shadow-sm'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center h-16'>
            <div className='flex items-center space-x-3 cursor-pointer' onClick={() => navigate('/')}>
              <img 
                src='https://customer-assets.emergentagent.com/job_smart-money-route/artifacts/6teoyzj7_Generated%20Image%20November%2018%2C%202025%20-%203_06PM.png' 
                alt='RouteFlow AI' 
                className='h-10 w-auto'
              />
              <span className='text-xl font-bold text-gray-900'>RouteFlow AI</span>
            </div>
            <Button variant='ghost' onClick={() => navigate(-1)}>
              <ArrowLeft className='w-5 h-5 mr-2' />
              Back
            </Button>
          </div>
        </div>
      </nav>

      <div className='max-w-4xl mx-auto px-4 py-12'>
        {/* Header */}
        <div className='text-center mb-12'>
          <h1 className='text-4xl font-bold text-gray-900 mb-4'>Fees & Pricing</h1>
          <p className='text-lg text-gray-600 max-w-2xl mx-auto'>
            Transparent pricing with no hidden fees. Know exactly what you'll pay before you send.
          </p>
        </div>

        {/* RouteFlow Fee */}
        <Card className='mb-8 border-2 border-emerald-500 bg-gradient-to-r from-emerald-50 to-teal-50'>
          <CardHeader>
            <CardTitle className='flex items-center gap-3 text-emerald-700'>
              <div className='w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center'>
                <Percent className='w-6 h-6 text-emerald-600' />
              </div>
              RouteFlow AI Service Fee
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-3xl font-bold text-emerald-600'>0.48%</p>
                <p className='text-gray-600 mt-1'>Applied to all transfers</p>
              </div>
              <div className='text-right'>
                <p className='text-sm text-gray-500'>Example: $100 transfer</p>
                <p className='text-lg font-medium text-gray-700'>Fee: $0.48</p>
              </div>
            </div>
            <div className='mt-4 pt-4 border-t border-emerald-200'>
              <p className='text-sm text-gray-600'>
                This is RouteFlow AI's service fee. Additional payment processing fees from our on-ramp and off-ramp partners will be added at checkout and vary depending on your payment method and geographic location.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Total Fee Example */}
        <Card className='mb-8 bg-gray-50'>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Info className='w-5 h-5 text-blue-500' />
              Example: Total Fees for $100 Transfer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-3'>
              <div className='flex justify-between py-2 border-b'>
                <span className='text-gray-600'>Transfer Amount</span>
                <span className='font-medium'>$100.00</span>
              </div>
              <div className='flex justify-between py-2 border-b'>
                <span className='text-gray-600'>RouteFlow AI Fee (0.48%)</span>
                <span className='font-medium'>-$0.48</span>
              </div>
              <div className='flex justify-between py-2 border-b'>
                <span className='text-gray-600'>Partner Processing Fee</span>
                <span className='font-medium text-gray-500'>Varies by location</span>
              </div>
              <div className='flex justify-between py-2 text-emerald-600 font-semibold'>
                <span>Recipient Gets</span>
                <span>Shown at checkout</span>
              </div>
            </div>
            <p className='text-xs text-gray-500 mt-4'>
              *Partner fees vary depending on your payment method, geographic location, and payout destination. The exact total will be displayed before you confirm your transfer.
            </p>
          </CardContent>
        </Card>

        {/* Key Points */}
        <div className='bg-white rounded-lg p-6 shadow-sm'>
          <h3 className='font-semibold text-gray-900 mb-4'>Key Points</h3>
          <div className='space-y-3'>
            <div className='flex items-start gap-3'>
              <CheckCircle className='w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0' />
              <p className='text-gray-600'>All fees are deducted from the transfer amount - you pay exactly what you enter</p>
            </div>
            <div className='flex items-start gap-3'>
              <CheckCircle className='w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0' />
              <p className='text-gray-600'>No hidden fees - exact total shown before you confirm</p>
            </div>
            <div className='flex items-start gap-3'>
              <CheckCircle className='w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0' />
              <p className='text-gray-600'>Daily limit: €15 - €10,000 | Monthly limit: €15,000</p>
            </div>
            <div className='flex items-start gap-3'>
              <CheckCircle className='w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0' />
              <p className='text-gray-600'>No KYC required for payments under €700/day (Visa, Mastercard, Revolut)</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className='text-center mt-12'>
          <Button 
            onClick={() => navigate('/')}
            className='bg-emerald-600 hover:bg-emerald-700 text-lg px-8 py-6'
          >
            Start Sending Money
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FeesPage;
